import React from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface PopularPost {
  id: string;
  title: string;
  image: string;
  imageAlt: string;
  slug: string;
  publishDate: string;
}

interface Category {
  name: string;
  count: number;
  slug: string;
}

interface BlogSidebarProps {
  popularPosts: PopularPost[];
  categories: Category[];
}

const BlogSidebar = ({ popularPosts, categories }: BlogSidebarProps) => {
  return (
    <aside className="space-y-6">
      {/* Newsletter Signup */}
      <div className="rounded-xl bg-gradient-to-br from-primary to-secondary p-6 text-primary-foreground shadow-warm">
        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-white/20">
          <Icon name="EnvelopeIcon" size={24} variant="solid" />
        </div>
        <h3 className="mb-2 font-heading text-xl font-semibold">
          Stay Updated
        </h3>
        <p className="mb-4 text-sm opacity-90">
          Get the latest Labrador care tips and puppy updates delivered to your inbox.
        </p>
        <form className="space-y-3">
          <input
            type="email"
            placeholder="Your email address"
            className="w-full rounded-md border-0 bg-white/20 px-4 py-2.5 text-primary-foreground placeholder:text-white/70 focus:outline-none focus:ring-3 focus:ring-white/50"
          />
          <button
            type="submit"
            className="flex h-11 w-full items-center justify-center gap-2 rounded-md bg-white font-medium text-primary shadow-warm transition-smooth hover:scale-[0.97]"
          >
            Subscribe
            <Icon name="PaperAirplaneIcon" size={18} variant="solid" />
          </button>
        </form>
      </div>

      {/* Popular Posts */}
      <div className="rounded-xl bg-card p-6 shadow-warm">
        <h3 className="mb-4 flex items-center gap-2 font-heading text-lg font-semibold text-foreground">
          <Icon name="FireIcon" size={20} variant="solid" className="text-accent" />
          Popular Posts
        </h3>
        <div className="space-y-4">
          {popularPosts.map((post) => (
            <Link
              key={post.id}
              href={`/blog/${post.slug}`}
              className="group flex gap-3 transition-smooth"
            >
              <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-md">
                <AppImage
                  src={post.image}
                  alt={post.imageAlt}
                  className="h-full w-full object-cover transition-smooth group-hover:scale-110"
                />
              </div>
              <div className="flex-1">
                <h4 className="mb-1 text-sm font-medium text-foreground transition-smooth group-hover:text-primary line-clamp-2">
                  {post.title}
                </h4>
                <time className="text-xs text-muted-foreground">
                  {new Date(post.publishDate).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </time>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Categories */}
      <div className="rounded-xl bg-card p-6 shadow-warm">
        <h3 className="mb-4 flex items-center gap-2 font-heading text-lg font-semibold text-foreground">
          <Icon name="FolderIcon" size={20} variant="solid" className="text-primary" />
          Categories
        </h3>
        <div className="space-y-2">
          {categories.map((category) => (
            <Link
              key={category.slug}
              href={`/blog/category/${category.slug}`}
              className="flex items-center justify-between rounded-md px-3 py-2 text-sm transition-smooth hover:bg-muted"
            >
              <span className="font-medium text-foreground">{category.name}</span>
              <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                {category.count}
              </span>
            </Link>
          ))}
        </div>
      </div>

      {/* Contact CTA */}
      <div className="rounded-xl bg-muted p-6">
        <h3 className="mb-2 font-heading text-lg font-semibold text-foreground">
          Have Questions?
        </h3>
        <p className="mb-4 text-sm text-muted-foreground">
          Our team is here to help you find the perfect Labrador puppy for your family.
        </p>
        <Link
          href="/contact-us"
          className="flex h-11 items-center justify-center gap-2 rounded-md bg-accent font-medium text-accent-foreground shadow-warm transition-smooth hover:scale-[0.97]"
        >
          Contact Us
          <Icon name="ChatBubbleLeftRightIcon" size={18} variant="solid" />
        </Link>
      </div>
    </aside>
  );
};

export default BlogSidebar;